<template>
    <div class="date-picker-menu">
        <div class="header">
            <div
                class="flipper left"
                @click="scrollPreviousMonth"
            >
                <Icon icon="angle-left" />
            </div>

            <div
                class="title"
                @click="isYearsPickerVisible = !isYearsPickerVisible"
            >
                {{ header }}
            </div>

            <div
                class="flipper right"
                @click="scrollNextMonth"
            >
                <Icon icon="angle-right" />
            </div>
        </div>

        <div class="months-container">
            <transition
                :name="transitionName"
                mode="out-in"
            >
                <VCalendarMonth
                    :key="dateViewCurrent.toString()"
                    :min="min"
                    :max="max"
                    :model-value="modelValue"
                    :date-view="dateViewCurrent"
                    :allowed-weekdays="allowedWeekdays"
                    :disallowed-dates="disallowedDates"
                    :allowed-dates="allowedDates"
                    @update:model-value="$emit('update:modelValue', $event)"
                />
            </transition>
        </div>

        <transition name="fade">
            <VCalendarYears
                v-if="isYearsPickerVisible"
                :model-value="modelValueYears"
                :year-min="min?.getFullYear()"
                :year-max="max?.getFullYear()"
                @pick="pickYear"
            />
        </transition>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { createTimelessDate, DAYS_ALL } from './date'
import { useDateFormat } from '@/composables/useDateFormat'

import VCalendarMonth from './VCalendarMonth.vue'
import VCalendarYears from './VCalendarYears.vue'
import Icon from '@/components/ui/icon/Icon.vue'

interface Props {
    modelValue: Date | null
    min?: Date | null
    max?: Date | null
    allowedWeekdays?: number
    disallowedDates?: Date[]
    allowedDates?: Date[]
}

const props = withDefaults(defineProps<Props>(), {
    min: undefined,
    max: undefined,
    allowedWeekdays: DAYS_ALL,
    disallowedDates: () => [],
    allowedDates: undefined,
})

const emit = defineEmits<{
    'update:modelValue': [payload: Date | null]
}>()

const pick = (date: Date) => {
    emit('update:modelValue', date)
}

// const getDateString = (year: number, month: number, date: number) =>
//     getDateStringWithOffset(year, month, date, props.offset)

const date = props.modelValue || new Date()

if (props.min && props.modelValue && props.modelValue < props.min) {
    pick(props.min)
    // date = props.min
}

if (props.max && props.modelValue && props.modelValue > props.max) {
    pick(props.max)
    // date = props.max
}

const formatDate = useDateFormat()
const dateViewCurrent = ref(
    createTimelessDate(new Date(date.getFullYear(), date.getMonth(), 1))
)
const header = computed(() =>
    formatDate(dateViewCurrent.value, {
        month: 'long',
        year: 'numeric',
    })
)

const transitionName = ref('slide')

const scrollNextMonth = () => {
    transitionName.value = 'slide'

    dateViewCurrent.value = new Date(
        dateViewCurrent.value.getFullYear(),
        dateViewCurrent.value.getMonth() + 1,
        dateViewCurrent.value.getDate()
    )
}

const scrollPreviousMonth = () => {
    transitionName.value = 'slide-reverse'

    dateViewCurrent.value = new Date(
        dateViewCurrent.value.getFullYear(),
        dateViewCurrent.value.getMonth() - 1,
        dateViewCurrent.value.getDate()
    )
}

const modelValueYears = computed(() => props.modelValue?.getFullYear() || null)

const isYearsPickerVisible = ref(false)
const pickYear = (year: number) => {
    const date = dateViewCurrent.value

    dateViewCurrent.value = new Date(year, date.getMonth(), date.getDate())

    isYearsPickerVisible.value = false
}
</script>

<style lang="scss" scoped>
.date-picker-menu {
    position: relative;
    height: 342px;
    border: 1px solid $blue;
    background-color: var(--white);
    font-family: $effra-font-stack;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0.05em;
    text-transform: uppercase;

    .header {
        position: relative;
        height: 42px;
        align-items: center;
        padding: 0 36px;
        display: flex;
        justify-content: space-between;
        text-transform: uppercase;
        font-weight: 500;
        user-select: none;
        z-index: 40;
        background-color: var(--stone-2);

        .title {
            cursor: pointer;
        }

        .flipper {
            height: 40px;
            width: 40px;
            border-radius: 100%;
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            user-select: none;
            pointer-events: all;

            &.disabled {
                pointer-events: none;
                color: var(--color-text-dark-disabled);
                cursor: default;
            }
        }
    }

    .months-container {
        position: relative;
        padding-bottom: 8px;

        .scroll-container {
            .calendars {
                transition: transform 300ms
                    var(--timing-function-easing-emphasized-reverse);
            }
        }
    }

    .flippers {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        pointer-events: none;
    }
}

.slide-enter-active,
.slide-leave-active,
.slide-reverse-enter-active,
.slide-reverse-leave-active {
    transition: all 75ms var(--timing-function-easing-accelerate);
}

.slide-enter-from,
.slide-reverse-leave-to {
    transform: translate(8px);
    opacity: 0;
}

.slide-leave-to,
.slide-reverse-enter-from {
    transform: translate(-8px);
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: all 150ms var(--timing-function-easing-accelerate);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
